const CSS_PROPERTY_NAME = /^-{0,2}[a-zA-Z_][\w-]*$/;

const SHORTHAND_TO_LONGHAND: Record<string, readonly string[]> = {
  background: [
    'background-color',
    'background-image',
    'background-repeat',
    'background-position',
    'background-size',
  ],
  padding: ['padding-top', 'padding-right', 'padding-bottom', 'padding-left'],
  margin: ['margin-top', 'margin-right', 'margin-bottom', 'margin-left'],
};

/** One nesting level — enough for typical `@keyframes { 0% { … } }`. */
const KEYFRAMES_BLOCK = /@keyframes\s+[\w-]+\s*\{(?:[^{}]|\{[^{}]*\})*\}/gi;

export function customCssText(css: unknown): string {
  return typeof css === 'string' ? css.trim() : '';
}

/** Scope user CSS to the frame: `&` → selector, wrap bare declarations, prefix keyframes. */
export function scopeFrameCustomCss(css: unknown, selector: string): string {
  const source = customCssText(css).replace(/<\/style/gi, '<\\/style');
  if (!source) {
    return '';
  }

  const withoutComments = source.replace(/\/\*[\s\S]*?\*\//g, '').trim();
  const isStylesheet = /[{@]|^\s*&/m.test(withoutComments);
  const scoped = isStylesheet ? replaceAmpersand(source, selector) : `${selector} {\n${source}\n}`;
  return prefixKeyframes(scoped, keyframePrefixFromSelector(selector));
}

/** Properties the stylesheet will set on the frame — omit these from inline styles. */
export function customCssStyleKeys(css: unknown): ReadonlySet<string> {
  const source = customCssText(css);
  if (!source) {
    return new Set();
  }

  const keys = new Set<string>();
  const withoutKeyframes = source.replace(KEYFRAMES_BLOCK, '');
  for (const match of withoutKeyframes.matchAll(/(?:^|[;{\s])(-{0,2}[a-zA-Z_][\w-]*)\s*:/g)) {
    const property = match[1];
    if (!property || !CSS_PROPERTY_NAME.test(property)) {
      continue;
    }
    keys.add(property);
    for (const longhand of SHORTHAND_TO_LONGHAND[property] ?? []) {
      keys.add(longhand);
    }
  }
  return keys;
}

export function omitInlineStylesOverriddenByCustomCss(
  styles: Record<string, string | null>,
  css: unknown,
): Record<string, string | null> {
  const keys = customCssStyleKeys(css);
  if (keys.size === 0) {
    return styles;
  }

  const next: Record<string, string | null> = {};
  for (const [key, value] of Object.entries(styles)) {
    if (!keys.has(key)) {
      next[key] = value;
    }
  }
  return next;
}

function replaceAmpersand(css: string, selector: string): string {
  return css.replace(/(^|[^\\])&/g, `$1${selector}`);
}

function keyframePrefixFromSelector(selector: string): string {
  return selector.replace(/^\./, '').replace(/[^a-zA-Z0-9_-]/g, '');
}

function prefixKeyframes(css: string, prefix: string): string {
  if (!prefix) {
    return css;
  }

  const names = new Set<string>();
  const withDefs = css.replace(/@keyframes\s+([A-Za-z_-][\w-]*)/g, (_match, name: string) => {
    names.add(name);
    return `@keyframes ${prefix}-${name}`;
  });
  if (names.size === 0) {
    return withDefs;
  }

  return withDefs.replace(
    /(animation(?:-name)?\s*:\s*)([^;}{]+)/gi,
    (_match, prop: string, value: string) => {
      let next = value;
      for (const name of names) {
        next = next.replace(
          new RegExp(`(^|[\\s,])(${escapeRegExp(name)})(?=[\\s,;]|$)`, 'g'),
          `$1${prefix}-${name}`,
        );
      }
      return `${prop}${next}`;
    },
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
